import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useFacilities } from "@/context/FacilityContext";
import { API_BASE } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash, Plus, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ScopePage({ scope, title, description, testPrefix }) {
  const { t } = useLang();
  const { facilities } = useFacilities();
  const [factors, setFactors] = useState([]);
  const [entries, setEntries] = useState([]);
  const [filterFacilityId, setFilterFacilityId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    factor_id: "",
    activity_data: "",
    date: new Date().toISOString().slice(0, 10),
    facility_id: "",
    notes: "",
    category: "",
    subcategory: "",
    data_quality: "secondary",
  });

  const load = async () => {
    const params = { scope };
    if (filterFacilityId) params.facility_id = filterFacilityId;
    const [f, e] = await Promise.all([
      api.get(`/factors`, { params: { scope } }),
      api.get(`/entries`, { params }),
    ]);
    setFactors(f.data);
    setEntries(e.data);
  };

  useEffect(() => {
    load();
  }, [scope, filterFacilityId]);

  const categories = useMemo(() => {
    const map = new Map();
    factors.forEach((f) => {
      if (!map.has(f.category)) map.set(f.category, []);
      map.get(f.category).push(f);
    });
    return Array.from(map.entries());
  }, [factors]);

  const selectedFactor = factors.find((f) => f.id === form.factor_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.factor_id || !form.activity_data) return;
    const f = factors.find((x) => x.id === form.factor_id);
    try {
      await api.post("/entries", {
        scope,
        category: f.category,
        subcategory: f.subcategory,
        factor_id: form.factor_id,
        activity_data: parseFloat(form.activity_data),
        date: form.date,
        facility_id: form.facility_id || null,
        notes: form.notes || null,
        data_quality: form.data_quality,
      });
      toast.success(t("entryAdded"));
      setShowForm(false);
      setForm({ ...form, factor_id: "", activity_data: "", facility_id: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    await api.delete(`/entries/${id}`);
    toast.success(t("entryDeleted"));
    load();
  };

  const total = entries.reduce((s, e) => s + (e.emissions_kg_co2e || 0), 0);

  return (
    <div className="space-y-6" data-testid={`${testPrefix}-page`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">GHG · {t("scope")} {scope}</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{title}</h1>
          <p className="text-muted-foreground mt-2 text-sm">{description}</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("totalEmissions")}</div>
          <div className="font-display text-3xl font-semibold font-mono-data">
            {(total / 1000).toFixed(3)} <span className="text-base text-muted-foreground">{t("tCO2e")}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display text-lg">{t("addEntry")}</CardTitle>
          <Button
            variant={showForm ? "outline" : "default"}
            size="sm"
            onClick={() => setShowForm((s) => !s)}
            data-testid={`${testPrefix}-toggle-form`}
          >
            <Plus size={16} className="mr-1" /> {showForm ? t("cancel") : t("addEntry")}
          </Button>
        </CardHeader>
        {showForm && (
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`${testPrefix}-form`}>
              <div className="md:col-span-2 space-y-2">
                <Label>{t("emissionFactor")}</Label>
                <Select value={form.factor_id} onValueChange={(v) => setForm({ ...form, factor_id: v })}>
                  <SelectTrigger data-testid={`${testPrefix}-factor-select`}>
                    <SelectValue placeholder={t("emissionFactor")} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(([cat, list]) => (
                      <React.Fragment key={cat}>
                        <div className="px-2 py-1 text-xs font-mono-data uppercase text-muted-foreground">{cat}</div>
                        {list.map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name} — {f.factor} kgCO₂e/{f.unit}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("activityData")} {selectedFactor && `(${selectedFactor.unit})`}</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  required
                  value={form.activity_data}
                  onChange={(e) => setForm({ ...form, activity_data: e.target.value })}
                  data-testid={`${testPrefix}-activity`}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("date")}</Label>
                <Input
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid={`${testPrefix}-date`}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("facility")}</Label>
                <Select value={form.facility_id || "none"} onValueChange={(v) => setForm({ ...form, facility_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid={`${testPrefix}-facility-select`}>
                    <SelectValue placeholder={t("unassigned")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— {t("unassigned")}</SelectItem>
                    {facilities.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}{f.code ? ` · ${f.code}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("dataQuality")}</Label>
                <Select value={form.data_quality} onValueChange={(v) => setForm({ ...form, data_quality: v })}>
                  <SelectTrigger data-testid={`${testPrefix}-dq-select`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">{t("primary")}</SelectItem>
                    <SelectItem value="secondary">{t("secondary")}</SelectItem>
                    <SelectItem value="tertiary">{t("tertiary")}</SelectItem>
                    <SelectItem value="estimated">{t("estimated")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t("notes")}</Label>
                <Textarea
                  rows={1}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid={`${testPrefix}-notes`}
                />
              </div>
              {selectedFactor && form.activity_data && (
                <div className="md:col-span-2 border border-primary/30 rounded-md p-4 bg-primary/5">
                  <div className="text-xs text-muted-foreground uppercase tracking-widest font-mono-data">Calculation</div>
                  <div className="font-mono-data text-sm mt-1">
                    {form.activity_data} {selectedFactor.unit} × {selectedFactor.factor} kgCO₂e/{selectedFactor.unit} ={" "}
                    <span className="font-semibold text-primary">
                      {(parseFloat(form.activity_data) * selectedFactor.factor).toFixed(3)} kgCO₂e
                    </span>
                  </div>
                </div>
              )}
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" data-testid={`${testPrefix}-submit`}>{t("save")}</Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="font-display text-lg">{title} — {entries.length} {t("records")}</CardTitle>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="space-y-1.5">
                <Label htmlFor={`${testPrefix}-facility-filter`} data-testid={`${testPrefix}-facility-filter-label`} className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
                  {t("facilityFilterLabel")}
                </Label>
                <Select value={filterFacilityId || "all"} onValueChange={(v) => setFilterFacilityId(v === "all" ? "" : v)}>
                  <SelectTrigger id={`${testPrefix}-facility-filter`} className="w-[220px]" data-testid={`${testPrefix}-facility-filter`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allFacilities")}</SelectItem>
                    {facilities.map((f) => (
                      <SelectItem key={`${testPrefix}-ff-${f.id}`} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (filterFacilityId) params.set("facility_id", filterFacilityId);
                  const qs = params.toString();
                  const url = `${API_BASE}/export/csv${qs ? `?${qs}` : ""}`;
                  const token = localStorage.getItem("cl_token") || "";
                  const orgId = localStorage.getItem("cl_active_org") || "";
                  fetch(url, {
                    credentials: "include",
                    headers: {
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      ...(orgId ? { "X-Org-Id": orgId } : {}),
                    },
                  })
                    .then((r) => r.blob())
                    .then((blob) => {
                      const link = document.createElement("a");
                      link.href = URL.createObjectURL(blob);
                      link.download = `scope${scope}_entries.csv`;
                      link.click();
                    });
                }}
                data-testid={`${testPrefix}-export-csv`}
              >
                <ArrowDown size={14} className="mr-1" /> {t("exportCsv")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">{t("noEntries")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2">{t("date")}</th>
                    <th className="text-left px-4 py-2">{t("category")}</th>
                    <th className="text-left px-4 py-2">{t("factor")}</th>
                    <th className="text-left px-4 py-2">{t("facility")}</th>
                    <th className="text-right px-4 py-2">{t("activityData")}</th>
                    <th className="text-center px-4 py-2">{t("dataQuality")}</th>
                    <th className="text-right px-4 py-2">{t("emissions")}</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors duration-150">
                      <td className="px-4 py-2 font-mono-data">{e.date}</td>
                      <td className="px-4 py-2">{e.category}</td>
                      <td className="px-4 py-2">{e.factor_name}</td>
                      <td className="px-4 py-2 text-right font-mono-data">
                        {e.activity_data} <span className="text-muted-foreground text-xs">{e.factor_unit}</span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-[10px] font-mono-data uppercase px-2 py-0.5 rounded-sm ${
                          e.data_quality === "primary" ? "bg-primary/10 text-primary" :
                          e.data_quality === "secondary" ? "bg-secondary text-foreground" :
                          e.data_quality === "tertiary" ? "bg-accent/10 text-accent" :
                          "bg-destructive/10 text-destructive"
                        }`}>
                          {(e.data_quality || "secondary").slice(0, 3)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono-data font-medium">
                        {e.emissions_kg_co2e.toFixed(3)} <span className="text-xs text-muted-foreground">kg</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => remove(e.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`${testPrefix}-delete-${e.id}`}
                        >
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
